// The menu: a 360 px tray sliding in from the right over a scrim; tapping
// the scrim or the close button closes it. Entries are links, actions or
// expandable sections whose content opens inside the scrolling tray. The
// play menu adds a row of icon tabs at the left of the header and a footer
// pinned below the scrolling entries.
import { useEffect, useRef, type ReactNode } from 'react';
import { Icon, type IconName } from '../components/Icon';
import { IconButton } from '../components/Button';
import './menu.css';

export interface MenuEntry {
  key: string;
  label: string;
  icon?: IconName;
  /** A smaller, quieter entry (the legal links). */
  small?: boolean;
  /** Marks a stub that does nothing yet. */
  soon?: boolean;
  /** A short value at the right of the label (a score, a count). */
  detail?: string;
  onSelect?: () => void;
  /** Expandable: content shown when open. */
  content?: ReactNode;
  open?: boolean;
}

export interface MenuTab {
  key: string;
  label: string;
  icon: IconName;
  disabled?: boolean;
}

interface Props {
  open: boolean;
  title?: string;
  entries: Array<MenuEntry | 'divider'>;
  onClose: () => void;
  tabs?: MenuTab[];
  activeTab?: string;
  onSelectTab?: (key: string) => void;
  /** Pinned below the entries, which scroll beneath it. */
  footer?: ReactNode;
}

export function MenuTray({ open, title, entries, onClose, tabs, activeTab, onSelectTab, footer }: Props) {
  const body = useRef<HTMLElement>(null);
  // A tab opens at its top.
  useEffect(() => {
    if (body.current) body.current.scrollTop = 0;
  }, [activeTab, open]);
  return (
    <div className={`tray-root${open ? ' is-open' : ''}`} aria-hidden={!open}>
      <div className="tray-scrim" onClick={onClose} />
      <aside className="tray panel" role="dialog" aria-label={title ?? 'Menu'}>
        <header className={`tray-header${tabs ? ' has-tabs' : ''}`}>
          {tabs && (
            <div className="tray-tabs" role="tablist" aria-label={title ?? 'Menu'}>
              {tabs.map((tab) => {
                const selected = tab.key === activeTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    id={`tray-tab-${tab.key}`}
                    aria-selected={selected}
                    aria-controls="tray-panel"
                    aria-label={tab.label}
                    title={tab.label}
                    className={`tray-tab${selected ? ' is-selected' : ''}`}
                    disabled={tab.disabled}
                    onClick={() => onSelectTab?.(tab.key)}
                  >
                    <Icon name={tab.icon} />
                  </button>
                );
              })}
            </div>
          )}
          <IconButton icon="close" label="Close menu" onClick={onClose} />
        </header>
        <nav
          ref={body}
          className="tray-entries"
          id={tabs ? 'tray-panel' : undefined}
          role={tabs ? 'tabpanel' : undefined}
          aria-labelledby={tabs && activeTab ? `tray-tab-${activeTab}` : undefined}
        >
          {entries.map((entry, i) =>
            entry === 'divider' ? (
              <hr key={`d${i}`} className="hairline tray-divider" />
            ) : (
              <div key={entry.key} className={`tray-entry${entry.small ? ' is-small' : ''}${entry.open ? ' is-open' : ''}`}>
                <button
                  type="button"
                  className="tray-item"
                  onClick={entry.onSelect}
                  disabled={!entry.onSelect}
                  aria-expanded={entry.content !== undefined ? !!entry.open : undefined}
                >
                  {entry.icon && <Icon name={entry.icon} small />}
                  <span className="tray-label">{entry.label}</span>
                  {entry.detail && <span className="tray-detail mono">{entry.detail}</span>}
                  {entry.soon && <span className="tag">soon</span>}
                  {entry.content !== undefined && <Icon name={entry.open ? 'caret-up' : 'caret-down'} small className="tray-caret" />}
                </button>
                {entry.content !== undefined && entry.open && <div className="tray-content">{entry.content}</div>}
              </div>
            ),
          )}
        </nav>
        {footer && <footer className="tray-footer">{footer}</footer>}
      </aside>
    </div>
  );
}
